import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { updateGenerationStatus, sendMessage } from './ChatSlice_Store';
import type { GenerationStatus } from './types/redux';
import { authenticatedFetch } from './utils/authErrorHandler';
import { DocumentTextIcon, CheckCircleIcon, ExclamationCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { AppDispatch } from './types/redux';

// Expected generation duration in seconds (used for progress estimation)
const EXPECTED_DURATION_SECONDS = 15 * 60;  // 15 minutes
const POLL_INTERVAL_MS = 5000;
const TIMEOUT_DURATION_MS = 20 * 60 * 1000; // 20 minutes (longer than expected duration)

// Module-level tracking - shared across all component instances
// This prevents duplicate SYSTEM_CHECK_DRAFT_STATUS messages when React renders multiple instances
const completionSentForProjects: Set<string> = new Set();

interface GenerationProgressBarProps {
  projectId: string;
  startTime: number;
  status: GenerationStatus;
  error?: string;
}

/**
 * Self-contained generation progress bar component.
 * Handles its own polling lifecycle - no global hooks needed.
 */
const GenerationProgressBar: React.FC<GenerationProgressBarProps> = ({
  projectId,
  startTime,
  status,
  error,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [progress, setProgress] = useState(0);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Handle completion - prevents duplicate processing
  const handleCompletion = (reason: 'success' | 'failed', errorMessage?: string) => {
    // Check module-level flag FIRST - this is synchronous and shared across all instances
    if (completionSentForProjects.has(projectId)) {
      return;
    }
    // Mark as sent immediately (synchronous, before any async operations)
    completionSentForProjects.add(projectId);

    // Clear poll timeout immediately to prevent further polls
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    // Update generation status
    if (reason === 'success') {
      dispatch(updateGenerationStatus({ projectId, status: 'processed' }));
    } else {
      dispatch(updateGenerationStatus({ projectId, status: 'failed', error: errorMessage }));
    }

    // Send system message to trigger agent to respond with completion message and action buttons
    // SYSTEM_ prefix makes it invisible to user while still triggering agent
    // Delay 10 seconds to allow backend to fully settle before agent checks status
    setTimeout(() => {
      dispatch(sendMessage('SYSTEM_CHECK_DRAFT_STATUS') as any);
    }, 4000);
  };

  // Poll API for status
  const pollStatus = async () => {
    // Check if already completed (prevents race conditions)
    if (completionSentForProjects.has(projectId) || status !== 'generating') {
      return;
    }

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const response = await authenticatedFetch(
        `${apiBaseUrl}/api/doc-drafter/status/${projectId}`
      );

      if (!response.ok) {
        // 404 means generation hasn't started yet - continue polling
        if (response.status === 404) {
          schedulePoll();
          return;
        }
        throw new Error(`Status check failed: ${response.status}`);
      }

      const data = await response.json();
      const processingStatus = data.processing_status;

      if (processingStatus === 'processed') {
        handleCompletion('success');
      } else if (processingStatus === 'failed') {
        handleCompletion('failed', data.draft_info?.processing_error || 'Generation failed');
      } else {
        // Still generating - continue polling
        schedulePoll();
      }
    } catch (err) {
      console.error('Error polling generation status:', err);
      // Continue polling on error (may be transient)
      schedulePoll();
    }
  };

  const schedulePoll = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
    }
    pollTimeoutRef.current = setTimeout(pollStatus, POLL_INTERVAL_MS);
  };

  // Progress animation effect
  useEffect(() => {
    if (status !== 'generating') {
      // Final states - set progress to 100 for success, keep current for failure
      if (status === 'processed') {
        setProgress(100);
      }
      return;
    }

    // Update progress based on elapsed time
    const updateProgress = () => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTime) / 1000);

      // Calculate progress using easing function (slows down near end)
      // This gives user feedback while polling continues
      const rawProgress = elapsed / EXPECTED_DURATION_SECONDS;
      // Use asymptotic curve: approaches 95% but never reaches it
      const easedProgress = Math.min(95, 100 * (1 - Math.exp(-2 * rawProgress)));
      setProgress(Math.round(easedProgress));

      // Check for timeout
      if (now - startTime > TIMEOUT_DURATION_MS) {
        dispatch(
          updateGenerationStatus({
            projectId,
            status: 'timeout',
            error: 'Generation timed out after 20 minutes',
          })
        );
      }
    };

    // Update progress every second
    updateProgress();
    progressIntervalRef.current = setInterval(updateProgress, 1000);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [status, startTime, projectId, dispatch]);

  // Polling effect
  useEffect(() => {
    if (status !== 'generating') {
      // Clean up poll timer when no longer generating
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
      return;
    }

    // Reset completion flag when starting a new generation session
    // (allows re-generation for the same project)
    completionSentForProjects.delete(projectId);

    // Start initial poll after delay to allow background task to create placeholder
    // The MCP generate_content returns immediately, but placeholder creation happens
    // in the background task - need to wait for it to be created in Snowflake
    pollTimeoutRef.current = setTimeout(pollStatus, 10000);

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [status, projectId]);

  // Get status display
  const getStatusDisplay = () => {
    switch (status) {
      case 'generating':
        return {
          text: 'Generating document...',
          icon: <ClockIcon className="w-5 h-5 text-blue-500 animate-pulse" />,
          barColor: 'bg-blue-500',
        };
      case 'processed':
        return {
          text: 'Document ready!',
          icon: <CheckCircleIcon className="w-5 h-5 text-green-500" />,
          barColor: 'bg-green-500',
        };
      case 'failed':
        return {
          text: error || 'Generation failed',
          icon: <ExclamationCircleIcon className="w-5 h-5 text-red-500" />,
          barColor: 'bg-red-500',
        };
      case 'timeout':
        return {
          text: 'Generation timed out',
          icon: <ExclamationCircleIcon className="w-5 h-5 text-orange-500" />,
          barColor: 'bg-orange-500',
        };
      default:
        return {
          text: 'Processing...',
          icon: <ClockIcon className="w-5 h-5 text-gray-500" />,
          barColor: 'bg-gray-500',
        };
    }
  };

  const { text: statusText, icon: statusIcon, barColor } = getStatusDisplay();

  return (
    <div className="w-full bg-white rounded-lg shadow-sm border border-gray-100 p-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <DocumentTextIcon className="w-5 h-5 text-gray-600" />
        <span className="text-sm font-medium text-gray-900">Document Generation</span>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${barColor}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Status Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {statusIcon}
          <span className="text-sm text-gray-700">{statusText}</span>
        </div>
        {status !== 'generating' && (
          <span className="text-sm font-medium text-gray-700">{progress}%</span>
        )}
      </div>
    </div>
  );
};

export default GenerationProgressBar;
